import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import ProductsLiveModeAppBar from "./ProductsLiveModeAppBar";
import { getAllModules } from "../../redux/moduleSlice";
import "./Products.css";

import RenderHtml from "../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import TypesFilter from "./TypesFilter";
import { buildPermissionContext, PERMISSIONS } from "../../util/permissions";
import { Search } from "lucide-react";
import { BASE } from "../../config";
import {
  getGlobalAppViewMode,
  setGlobalAppViewMode,
} from "../../util/appViewModeStorage";

const Products = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get products from Redux state
  const {
    modules: products,
    loading,
    error,
  } = useSelector((state) => state.module);
  const authUser = useSelector((state) => state.auth.user);

  // Fetch products when component mounts
  useEffect(() => {
    dispatch(getAllModules({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  const pageContainer = useRef();
  const [useClassicAppbar, setUseClassicAppbar] = useState(() => {
    const g = getGlobalAppViewMode();
    if (g !== null) return !g;
    return sessionStorage.getItem("products_use_classic_appbar") === "true";
  });
  const [filterState, setFilterState] = useState({
    filterByType: "all",
    typeFilters: {},
  });

  const switchToClassicAppbar = () => {
    setGlobalAppViewMode(false);
    setUseClassicAppbar(true);
  };

  const switchToLiveModeAppbar = () => {
    setGlobalAppViewMode(true);
    setUseClassicAppbar(false);
  };

  // Navigate to Search Query Page — pass Live vs Edit so SearchQuery columns match Directory app bar.
  const handleSearchClick = () => {
    navigate("/search", {
      state: { isLiveMode: !useClassicAppbar },
    });
  };

  // Get user's module_type_permissions - memoized to update when authUser changes
  const moduleTypePermissions = useMemo(() => {
    try {
      if (authUser?.module_type_permissions) {
        return authUser.module_type_permissions;
      }
      const stored = localStorage.loggedinUserInfo;
      if (stored) {
        const userInfo = JSON.parse(stored);
        return userInfo?.module_type_permissions || {};
      }
    } catch (error) {
      console.error("Failed to get module type permissions", error);
    }
    return {};
  }, [authUser]);

  // Check if user has view permission for a type
  const hasViewPermission = (typeName) => {
    if (!typeName) return false;
    return moduleTypePermissions[typeName]?.view === true;
  };

  // Check if user has edit permission for a type
  const hasEditPermission = (typeName) => {
    if (!typeName) return false;
    return moduleTypePermissions[typeName]?.edit === true;
  };

  const isHiddenFromLiveMode = (product) => {
    const v =
      product?.is_hide_from_live_mode ??
      product?.hide_from_live_mode ??
      product?.hide_from_live;
    return v === true || String(v).toLowerCase() === "true" || v === 1 || v === "1";
  };

  // Filter products based on module_type_permissions (view or edit permission)
  // Show modules where the user has view OR edit permission for the module_part.type_name
  const getFilteredProductsByPermission = (products) => {
    if (!products || products.length === 0) return [];
    return products.filter((product) => {
      const typeName = product.module_part?.type_name;
      if (!typeName) return false;
      // Show if user has view OR edit permission for this type
      return hasViewPermission(typeName) || hasEditPermission(typeName);
    });
  };

  function filterProducts(products, filterState) {
    // First filter by permission
    let permissionFiltered = getFilteredProductsByPermission(products);
    // In live mode, always hide modules marked "Hide From Live Mode".
    if (!useClassicAppbar) {
      permissionFiltered = permissionFiltered.filter(
        (product) => !isHiddenFromLiveMode(product),
      );
    }

    if (filterState.filterByType === "all") return permissionFiltered;
    if (filterState.filterByType === "Select Types") {
      return permissionFiltered.filter((item) => {
        const typeName = item.module_part?.type_name;
        return filterState.typeFilters[typeName] === true;
      });
    }
    return [];
  }
  const permissionCtx = buildPermissionContext(authUser);
  const canStrictCreate =
    permissionCtx.permissions?.[PERMISSIONS.CREATE] === true;
  const canStrictView = permissionCtx.permissions?.[PERMISSIONS.VIEW] === true;
  const canStrictEdit = permissionCtx.permissions?.[PERMISSIONS.EDIT] === true;

  function groupByProject(products) {
    return products.reduce((acc, product) => {
      const projectName = product.project?.description || "Unknown Project";
      if (!acc[projectName]) acc[projectName] = [];
      acc[projectName].push(product);
      return acc;
    }, {});
  }
  const filteredProducts = filterProducts(products, filterState);
  const groupedByProject = groupByProject(filteredProducts);

  // Only populate typeFilters with types the user has view OR edit permission for
  useEffect(() => {
    const allowedTypes = {};
    if (products && products.length > 0) {
      products.forEach((product) => {
        const typeName = product.module_part?.type_name;
        // Include types where user has view OR edit permission
        if (
          typeName &&
          (moduleTypePermissions[typeName]?.view === true ||
            moduleTypePermissions[typeName]?.edit === true)
        ) {
          allowedTypes[typeName] = true;
        }
      });
    }

    setFilterState((prev) => ({
      ...prev,
      typeFilters: allowedTypes,
    }));
  }, [products, moduleTypePermissions]);

  useEffect(() => {
    // scrollToTop({current:document.querySelector('html')})
  }, [filterState]);

  const handleClick = (product, isUnlinkCard = false) => {
    // For "Not Assigned" section, check type-specific view or edit permission
    if (isUnlinkCard) {
      const hasView = hasViewPermission("Not Assigned");
      const hasEdit = hasEditPermission("Not Assigned");
      if (!hasView && !hasEdit) {
        // Disable navigation if neither View nor Edit permission is true for this type
        return;
      }
    } else {
      // For regular products, check type-specific view or edit permission
      const typeName = product?.module_part?.type_name;
      if (typeName) {
        const hasView = hasViewPermission(typeName);
        const hasEdit = hasEditPermission(typeName);
        if (!hasView && !hasEdit) {
          // Disable navigation if neither View nor Edit permission is true for this type
          return;
        }
      } else {
        // Fallback: check general View or Edit permission if type name is not available
        const hasGeneralView = permissionCtx.permissions?.View === true;
        const hasGeneralEdit = canStrictEdit;
        if (!hasGeneralView && !hasGeneralEdit) {
          return;
        }
      }
    }

    navigate("/new-module", {
      state: {
        moduleData: product || {},
        isUnlink: isUnlinkCard,
        isViewMode: !useClassicAppbar,
      },
    });
  };

  const img = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIoAAACJCAYAAADzE6DKAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAU+SURBVHhe7Z2LcqJAEEX3/38zLzWJJhpfSZStm8IqwUYbnIEBzqm6VckmiEufSA8j478MwAGigItaohwOh2y73WaLxSKbTqfZ8/Nz9vj4mD08PJBEo/qoTqqX6qb6qY51cYlyPB6z5XL5t0PryZB+RXVUPVVXLzdF2Ww22WQyMXdI+h3VVfX1cFWU1Wpl7oAMK6rzLSpF0bnMelAyzKje1zBF2e/35oORYUd1r+JCFDU49CTjjOpe1eBeiKJu2HoQMo6o/hYFUTS+Zgg87qj+1nWWgijr9drcmIwr8qBMQZT5fG5uSMYVeVCmIIou81obXsvb21v2+flJEo3qY9XtWuRBmYIodfuT3W6XbwkpozpZ9auKPChTEKXOBN9sNsu3gj6gell1tCIPyhREsTaqil7WoD+oXlYdq1IGUUYCooALRAEXiAIuEAVcIAq4QBRwgSjgAlHABaKAC0QBF4gCLhAFXCAKuEAUcIEo4AJRwAWigAtEAReIAi4QBVwgCrhAFHCBKOACUcAFooALRAEXiAIuEAVcIAq4QBRwgSjgAlHABaKAC0QBF4gCLhAFXCAKuEAUcIEo4AJRwAWigAtEAReIAi4QBVwgCrhAFHCBKOACUcAFooALRAEXiAIuEAVcIEpirFar/Ku0QJSEOH1q+X6/z/8lHRAlEc4LYX0SedcgSgJst9uL4/P+/p7/NA0QpWMOh8PfR9RbxyilfgVROmY2m5nH55RU+hVE6ZDFYmEem/O8vLzkv90tiNIR6/XaPC5WUuhXRi3K7+9v/lW7/Pz8mMfkWrruV0Yriv6ip9Np/l27TCYT85jcSpf9yihFOf+LVp/QJvP5vHAc6qTLfmWUouiV5Py5fH195T+Ji04f5/ttkq76ldGJUjXS+P7+zn8jDjptWPttkrbEPmdUougAW89Fifmyfjwe/y7LW/ttmrb7ldGIolcM63mcR5NyMThN9oVM2/3KaETxjjSWy2W+RRjqHuA6UWPcFqMQRQ2g9Ryqokm6EFiTfaHTVr8yeFGajDSenp7+JuvuQRfzqib7QqeNfmXQotwz0tBk3T3cmuwLGZ1WYzNYUTTSUMNn7dubj4+P/NHq4ZnsC53Y/cpgRQk10tCl/jrUmewLnZj9yiBF0cjF2mfT6JK/B88QPHZi9SuDE2W325n7uyfeHqDpZF/IxOpXBiWKRiqhr4CecqsHqDsEj5kY/cqgRHl9fTX3FSpV7wkJMdkXOqH7lcGIUvc/0jTlHuCeIXjshOxXBiHKZrMx9xEjOrVp6C1iTPaFTMg3ZvVeFF0B1ZVUax+xolOciH2qC5FQ/UrvRWnzCuh56h64LhOiX+m1KLpyaj02ucy9/UpvRenyCmgfc2+/0ktRdAW0rZnZIeWefqWXopTfHE38adqv9E4U/VVYj0f8adKv9PIVBdoHUcAFooALRAEXiAIuEAVcIAq4QBRwgSjgAlHABaKAC0QBF4gCLhAFXCAKuAgqSp13ncVaBgviUOemf3lQpiBK3XtcdJ8wpE/d+7nlQZmCKE3eoqjbLfSyRtJMk9thrDdyF0TpYgEZkl6s1cALorSxuB1JP9ZiiQVRYi47QfoR1d9aKLEgigi92hHpV6rW6b0QRXf4p7DyEGk/qvtppYcyF6KINpehIOlEda/CFEWkuAoRiZeq1ahOVIoiGAWNI54l4a+KInT7Ij3LMKO6em9PvSmKUIOjbpih8zCiOqqeVY2rhUuUExpfa10T3Wiuy7zaIctXpB3VR3VSvVQ31a/JB0rUEgXGC6KAC0QBF4gCDrLsP+CYxLdKyo7VAAAAAElFTkSuQmCC`;

  const directoryViewerName =
    localStorage.getItem("user_name") ||
    [authUser?.first_name, authUser?.last_name].filter(Boolean).join(" ") ||
    "";

  return (
    <div ref={pageContainer} className="products-directory">
      {useClassicAppbar ? (
        <AppBar
          showBackButton={false}
          showBookmark={true}
          onBookmarkClick={switchToLiveModeAppbar}
          showBlackTitle={true}
          title="Directory"
          showTablesDropdown={true}
          subTitleAboveTitle={<>{directoryViewerName} is Viewing</>}
          showQuestionIcon={false}
          questionIconDummy={true}
          buttons={[
            {
              label: "New Module",
              path: "/new-module",
              disabled: !canStrictCreate,
              title: !canStrictCreate
                ? "You do not have permission to create modules."
                : undefined,
            },
            {
              label: "New Document",
              path: "/new-procedure",
              disabled: !canStrictCreate,
              title: !canStrictCreate
                ? "You do not have permission to create documents."
                : undefined,
            },
          ]}
        />
      ) : (
        <ProductsLiveModeAppBar
          viewerName={directoryViewerName}
          onEditModeClick={switchToClassicAppbar}
        />
      )}

      {/* Search Bar */}
      <div className="search-bar products-search-bar">
        <input
          type="text"
          placeholder="Enter Document Item Code or Description"
          onClick={handleSearchClick}
          readOnly
          style={{ border: "none", outline: "none" }}
        />
        <div className="search-icon-right">
          <Search size={18} color="grey" />
        </div>
      </div>
      <div className="products-section">
        {loading && <p>Loading modules...</p>}
        {error && <p className="error">{error}</p>}

        {!loading && Object.keys(groupedByProject).length === 0 && (
          <p>No modules available.</p>
        )}

        {Object.entries(groupedByProject).map(
          ([projectName, projectModules]) => (
            <div key={projectName} className="project-group">
              <h3 className="project-heading">{projectName}</h3>

              <div className="products-grid">
                {projectModules.map((product, index) => {
                  const typeName = product.module_part?.type_name;
                  const hasModuleViewPermission = hasViewPermission(typeName);
                  const hasModuleEditPermission = hasEditPermission(typeName);
                  // Module is visible if user has view OR edit permission
                  const isModuleVisible =
                    hasModuleViewPermission || hasModuleEditPermission;

                  return (
                    <div
                      key={index}
                      onClick={() => handleClick(product)}
                      style={{
                        opacity: isModuleVisible ? 1 : 0.5,
                        pointerEvents: isModuleVisible ? "auto" : "none",
                        cursor: isModuleVisible ? "pointer" : "not-allowed",
                      }}
                      className="product-card"
                      title={
                        !isModuleVisible
                          ? "You do not have permission to view or edit this module."
                          : !hasModuleEditPermission
                          ? "You can view this module but do not have permission to edit it."
                          : undefined
                      }
                    >
                      {
                        <img
                          src={
                            product.product_image &&
                            product.product_image.includes("http")
                              ? product.product_image
                              : `${BASE}${product.product_image}`
                          }
                          alt={product.product_image}
                          className={
                            product.product_image ? "product-image" : ""
                          }
                          onError={(event) => {
                            event.target.error = null;
                            event.target.src = img;
                            event.target.style.width = "width: 130px;";
                            event.target.style.height = "130px !important";
                            event.target.classList.remove("product-image");
                          }}
                          style={{
                            width: "100%",
                          }}
                        />
                      }
                      <h4>{product.module_part.part_number}</h4>
                      <p>
                        <RenderHtml content={product.module_part.description} />
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}

        {(hasViewPermission("Not Assigned") ||
          hasEditPermission("Not Assigned")) && (
          <>
            <h3>No Module Assigned</h3>
            <div
              className="no-module-container"
              style={{
                opacity:
                  hasViewPermission("Not Assigned") ||
                  hasEditPermission("Not Assigned")
                    ? 1
                    : 0.5,
              }}
              title={
                !hasViewPermission("Not Assigned") &&
                !hasEditPermission("Not Assigned")
                  ? "You do not have permission to view or edit modules."
                  : !hasEditPermission("Not Assigned")
                  ? "You can view this module but do not have permission to edit it."
                  : undefined
              }
            >
              <div onClick={() => handleClick(null, true)}>
                <div className="no-module-card">
                  <img
                    alt="Not Assigned"
                    src="data:image/png;base64,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"
                  />
                </div>

                <div className="no-module-info">
                  <p className="no-module-title">
                    <span className="no-module-hidden">Not Assigned</span>
                    <span className="no-module-badge"> (Hidden)</span>
                  </p>
                  <p className="no-module-description">
                    Document not assigned any Module
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <TypesFilter filterState={filterState} setFilterState={setFilterState} />
    </div>
  );
};

export default Products;
